/**
 * Add Program fields by program type.
 * Select options are only the values visible on that program's form.
 * Dates, amounts, and tier rows shown filled in the mocks are placeholders, not defaults.
 */

const date = (key, label, extra = {}) => ({ key, label, kind: "date", ...extra });
const text = (key, label, extra = {}) => ({ key, label, kind: "text", ...extra });
const select = (key, label, options, placeholder) => ({ key, label, kind: "select", options, placeholder });
const percent = (key, label, placeholder, hint) => ({
  key,
  label,
  kind: "percent",
  placeholder,
  hint,
  adornment: "percent",
});
const money = (key, label, placeholder, extra = {}) => ({
  key,
  label,
  kind: "currency",
  placeholder,
  adornment: "dollar",
  ...extra,
});

const accounts = (hint) => ({
  key: "payerAccountNumbers",
  label: "AWS Payer / Payee Accounts",
  kind: "accounts",
  placeholder: "Select accounts...",
  hint,
});

const regions = (hint, label = "Assign Regions") => ({
  key: "regions",
  label,
  kind: "regions",
  placeholder: "Select regions...",
  hint,
});

const services = (key, label, placeholder, hint) => ({
  key,
  label,
  kind: "services",
  placeholder,
  hint,
  noOptionsText: "No services are available",
});

const pair = (fields) => ({ columns: { xs: "1fr", sm: "1fr 1fr" }, fields });
const triple = (fields) => ({ columns: { xs: "1fr", sm: "1fr 1fr 1fr" }, fields });
const quad = (fields) => ({
  columns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
  fields,
});
const stack = (field) => ({ fields: [field] });

const PPA = {
  subtitle: "Register a new distribution agreement and contract terms",
  saveLabel: "Create PPA",
  sections: [
    {
      title: "General Program Information",
      blocks: [
        stack(text("displayName", "Program Display Name", {
          placeholder: "e.g., AWS Tier-1 Global Distribution Master Agreement - 2026",
        })),
        pair([
          text("agreementId", "Agreement ID", {
            placeholder: "e.g., PPA-AWS-DIST-8842",
          }),
          text("referenceNumber", "Reference Number", {
            placeholder: "e.g., REF-PPA-2026-001",
          }),
        ]),
        stack(accounts("Master AWS payer accounts governed by this PPA agreement")),
        stack(regions("Regions and contracting entities where this PPA agreement applies")),
        stack({
          key: "eligibleServices",
          label: "Eligible Services",
          kind: "services",
          placeholder: "Search services...",
          hint: "Solution Provider and Distributor eligible services for private pricing",
          noOptionsText: "No services match your search",
        }),
      ],
    },
    {
      title: "Contract Terms",
      blocks: [
        triple([
          date("startDate", "Start Date", { placeholder: "Select the Start Date" }),
          date("endDate", "End Date", { placeholder: "Select the End Date" }),
          select(
            "renewalNotification",
            "Renewal Notification Window",
            ["30", "60", "90"],
            "Select days..."
          ),
        ]),
      ],
    },
    {
      title: "Discounts and Terms",
      blocks: [
        triple([
          percent("baselineDiscount", "Baseline Distribution Discount (%)", "15.00", "Wholesale discount on eligible service usage"),
          money("annualCommitment", "Commitment ($)", "2,500,000", {
            hint: "Minimum annual wholesale consumption",
            amountError: "Enter a commitment amount.",
          }),
          select("paymentTerms", "Payment Terms (Net Days)", ["Net 30", "Net 45", "Net 60", "Net 90", "Net 180"], "Select terms..."),
        ]),
      ],
    },
    {
      title: "Annual Commitment Breakdown",
      action: "addTier",
      blocks: [{ kind: "tiers", variant: "ppa" }],
    },
  ],
};

const SPP = {
  subtitle: "Configure a new Solution Provider Program agreement and commercial terms",
  saveLabel: "Create SPP",
  sections: [
    {
      title: "General Program Information",
      blocks: [
        stack(text("displayName", "Program Display Name", {
          placeholder: "e.g., AWS SPP Tier 2 Reseller Agreement - 2026/2027",
        })),
        pair([
          text("agreementId", "Agreement ID", {
            placeholder: "e.g., SPP-AWS-RES-5021",
          }),
          text("referenceNumber", "Reference Number", {
            placeholder: "e.g., REF-SPP-2026-001",
          }),
        ]),
        stack(accounts("Master and Payee AWS accounts linked to and governed by this SPP wholesale contract")),
        stack(regions(
          "Geographic scope and regional AWS contracting entities governed under this SPP agreement",
          "Regions Selection"
        )),
        stack({
          key: "eligibleServices",
          label: "SPP Eligible Services Checklist",
          kind: "services",
          placeholder: "Search services...",
          hint: "CUR-intersected SPP-eligible services for private pricing discount rules",
          noOptionsText: "No services match your search",
        }),
      ],
    },
    {
      title: "Contract Terms & Validity",
      blocks: [
        triple([
          date("startDate", "Start Date", { placeholder: "Select the Start Date" }),
          date("endDate", "End Date", { placeholder: "Select the End Date" }),
          select(
            "renewalNotification",
            "Renewal Notification Window",
            ["30", "60", "90"],
            "Select days..."
          ),
        ]),
      ],
    },
    {
      title: "Baseline & Financial Terms",
      blocks: [
        triple([
          select(
            "partnerTierLevel",
            "Partner Tier Level",
            ["Tier 1 Registered", "Tier 2 Advanced", "Premier"],
            "Select tier level..."
          ),
          percent(
            "baselineDiscount",
            "Baseline Partner Discount (%)",
            "12.50",
            "Guaranteed starting wholesale discount under the SPP"
          ),
          money("annualCommitment", "Annual Spend Commitment ($)", "2,000,000", {
            hint: "Minimum annual wholesale consumption to maintain program standing",
            amountError: "Enter a commitment amount.",
          }),
        ]),
      ],
    },
    {
      title: "Volume Discount Tiers",
      action: "addTier",
      blocks: [{ kind: "tiers", variant: "spp" }],
    },
    {
      title: "Growth Bonus Incentives & Milestones",
      blocks: [
        pair([
          select(
            "growthTriggerMetric",
            "Growth Trigger Metric",
            [
              "Spend Target",
              "Workload Modernization (Bedrock/Containers)",
              "Migration Count",
            ],
            "Select metric..."
          ),
          text("targetMilestoneValue", "Target Milestone Value", {
            placeholder: "e.g., $2,000,000 or 10 Workloads",
          }),
        ]),
        pair([
          percent(
            "growthBonusRate",
            "Growth Bonus Rate (%)",
            "2.50",
            "Accelerated reward percentage once the milestone is achieved"
          ),
          select(
            "payoutStructure",
            "Payout Structure",
            ["Retroactive Discount", "Incremental Acceleration", "Back-End Rebate Credit"],
            "Select payout structure..."
          ),
        ]),
        stack(services(
          "qualifyingServices",
          "Qualifying Target Services Filter",
          "Search services...",
          "CUR services that count toward the growth milestone"
        )),
      ],
    },
    {
      title: "Contract Rules",
      blocks: [
        stack({
          key: "expirationOverride",
          label: "Contract Expiration Override Rule",
          kind: "switch",
          hint: "Continue discount calculations for services retiring near-term when the active SPP contract extends past the service retirement date",
        }),
      ],
    },
  ],
};

const EDP = {
  subtitle: "Configure a new Enterprise Discount Program agreement and commitment terms",
  saveLabel: "Create EDP",
  sections: [
    {
      title: "General Program Information",
      blocks: [
        stack(text("displayName", "Program Display Name", {
          placeholder: "e.g., Global Enterprise Direct Discount - 2026-2029",
        })),
        pair([
          text("agreementId", "Agreement ID", {
            placeholder: "e.g., EDP-AWS-ENT-9912",
          }),
          text("referenceNumber", "Reference Number", {
            placeholder: "e.g., REF-EDP-2026-001",
          }),
        ]),
        stack(accounts("Master and linked AWS Payer accounts covered under the EDP commitment pool")),
        stack(regions(
          "Geographic scope or global application of the EDP commitment",
          "Regions Selection"
        )),
        stack({
          key: "eligibleServices",
          label: "Eligible Services",
          kind: "services",
          placeholder: "Search services...",
          hint: "EDP-eligible services covered under this commitment; typically excludes Support, Marketplace, and certain taxes/fees",
          noOptionsText: "No services match your search",
        }),
      ],
    },
    {
      title: "Contract Terms & Validity",
      blocks: [
        quad([
          date("startDate", "Start Date", { placeholder: "Select the Start Date" }),
          date("endDate", "End Date", { placeholder: "Select the End Date" }),
          select(
            "renewalNotification",
            "Renewal Notification Window",
            ["30", "60", "90"],
            "Select days..."
          ),
          select(
            "trueUpWindow",
            "True-Up Window",
            [
              "30 days after the period ends",
              "60 days after the period ends",
              "90 days after the period ends",
            ],
            "Select true-up window..."
          ),
        ]),
      ],
    },
    {
      title: "Financial Commitment & Tiered Discount Structure",
      blocks: [
        quad([
          money("multiYearCommitment", "Total Multi-Year Commitment ($)", "15,000,000", {
            hint: "Total agreed spend commitment across the entire contract term",
            amountError: "Enter a commitment amount.",
          }),
          money("annualConsumptionTarget", "Annual Consumption Target ($)", "5,000,000", {
            hint: "Expected annual spend to stay on track for the total commitment",
            amountError: "Enter a consumption target.",
          }),
          percent(
            "edpDiscount",
            "Flat EDP Discount (%)",
            "18.00",
            "Guaranteed discount across eligible AWS services for the EDP term"
          ),
          select(
            "paymentTerms",
            "Payment Terms (Net Days)",
            ["Net 30", "Net 45", "Net 60", "Net 90", "Net 180"],
            "Select terms..."
          ),
        ]),
        pair([
          select(
            "trueUpModel",
            "Commitment True-Up Model",
            ["Annual True-Up", "End-of-Term True-Up", "True-Up Billing Mechanism"],
            "Select true-up model..."
          ),
          select(
            "shortfallRule",
            "Shortfall Penalty Management Rule",
            ["Invoice True-Up Charge", "Rollover to Next Year"],
            "Select shortfall rule..."
          ),
        ]),
        stack({
          key: "rolloverUnused",
          label: "Rollover Unused Commitment to Next Period",
          kind: "switch",
        }),
      ],
    },
  ],
};

const RI = {
  subtitle: "Configure a Reserved Instance Margin Program with coverage and waste tracking",
  saveLabel: "Create RI",
  sections: [
    {
      title: "General Program Information",
      blocks: [
        stack(text("displayName", "Program Display Name", {
          placeholder: "e.g., AWS Compute Savings & RI Pool - 2026",
        })),
        pair([
          text("agreementId", "Agreement ID", {
            placeholder: "e.g., RI-AWS-POOL-4412",
          }),
          text("referenceNumber", "Reference Number", {
            placeholder: "e.g., REF-RI-2026-001",
          }),
        ]),
        stack(accounts("Master and payee AWS accounts linked to and covered under this RI/Savings Plan purchasing pool")),
        stack(regions(
          "Geographic scope or regional boundary where the RI commitments apply",
          "Regions Selection"
        )),
      ],
    },
    {
      title: "Term & Validity",
      blocks: [
        triple([
          date("startDate", "Effective Start Date", { placeholder: "Select the Start Date" }),
          date("endDate", "Expiration Date", { placeholder: "Select the End Date" }),
          {
            key: "expirationAlertWindow",
            label: "Renewal / Expiration Alert Window",
            kind: "days",
            placeholder: "60",
            hint: "Days before expiration for renewal and portfolio re-evaluation alerts",
            hintRow: true,
          },
        ]),
      ],
    },
    {
      title: "RI Portfolio Parameters",
      blocks: [
        pair([
          select(
            "riCommitmentType",
            "RI Commitment Type",
            ["Standard RI", "Convertible RI", "Compute Savings Plan / RI Blended"],
            "Select commitment type..."
          ),
          select(
            "paymentOption",
            "Payment Option",
            ["All Upfront", "Partial Upfront", "No Upfront"],
            "Select payment option..."
          ),
        ]),
        pair([
          select("termLength", "Term Length", ["1-Year", "3-Year"], "Select term length..."),
          select(
            "amortizationMethod",
            "Amortization Tracking Method",
            ["Straight-Line Monthly", "Cash-Flow Realized"],
            "Select amortization method..."
          ),
        ]),
      ],
    },
    {
      title: "Coverage & Waste Alerts",
      blocks: [
        pair([
          percent(
            "targetCoverage",
            "Target Coverage Percentage (%)",
            "85.00",
            "Desired share of eligible on-demand workloads covered by reserved capacity"
          ),
          percent(
            "wasteThreshold",
            "Unallocated / Waste Threshold Alert (%)",
            "10.00",
            "Maximum unused or unassigned RI capacity before an operational waste alert"
          ),
        ]),
      ],
    },
    {
      title: "Eligible Services & Scope",
      blocks: [
        stack(services(
          "riEligibleServices",
          "RI Eligible Services Master Picker",
          "Search services...",
          "Instance families, database types, or cache nodes eligible for reservation matching"
        )),
        stack({
          key: "crossAccountSharing",
          label: "Cross-Account Sharing Enabled",
          kind: "switch",
          hint: "Allow reserved capacity to float and apply discounts across linked accounts in the organization hierarchy",
        }),
      ],
    },
  ],
};

const FORMS = { PPA, SPP, EDP, RI };

export const programForm = (code) => FORMS[code] || PPA;

export const fieldsIn = (config) => {
  const fields = [];
  config.sections.forEach((section) => {
    section.blocks.forEach((block) => {
      if (block.kind === "tiers" || block.kind === "years") fields.push(block);
      else fields.push(...block.fields);
    });
  });
  return fields;
};
